import { ArtifactChoice, MultipleChoiceArtifact } from '@dreamai/types';

/**
 * Hotel payload carried by choices of a `hotels` multiple-choice artifact.
 *
 * Mirrors `HotelOffer` & friends in dream-types (`api/response-artifacts.ts`,
 * dream-types#74). Switch to `import from '@dreamai/types'` once that version
 * is published and bumped here.
 */

export type HotelBookingMode = 'instant' | 'agent';

export type HotelRatingSource = 'booking_com' | 'google';

export interface HotelGuestRating {
  score: number;
  outOf: 5 | 10;
  reviewCount?: number | null;
  source: HotelRatingSource;
}

export interface HotelOfferRate {
  offerId: string;
  board: string;
  refundable: boolean;
  freeCancellationUntil?: string | null;
  cancellationTerms: string;
  total: number;
  perNight?: number | null;
  currency: string;
  payAtHotel?: boolean;
}

export interface HotelOfferRoom {
  roomId?: string | null;
  name: string;
  description?: string | null;
  sizeSqm?: number | null;
  beds?: string | null;
  sleeps?: number | null;
  photo?: string | null;
  photos?: string[];
  amenities?: string[];
  rates: HotelOfferRate[];
}

export interface HotelOfferSource {
  name: string;
  total?: number | null;
  currency: string;
  official?: boolean;
  url?: string | null;
  refundable?: boolean | null;
}

export interface HotelOfferStay {
  checkInDate: string;
  checkOutDate: string;
  nights: number;
  adults: number;
  children: number;
}

export interface HotelOffer {
  bookingMode: HotelBookingMode;
  stay: HotelOfferStay;
  stars?: number | null;
  address?: string | null;
  description?: string | null;
  facilities?: string[];
  photos?: string[];
  rating?: HotelGuestRating | null;
  rooms?: HotelOfferRoom[];
  sources?: HotelOfferSource[];
  cheapestOfferId?: string | null;
  hasFreeCancellationRate: boolean;
}

export type HotelChoice = ArtifactChoice & {
  choiceBlurb?: string;
  fromPrice?: number;
  hotelOffer?: HotelOffer;
};

/** Who is staying — sent with the Book press, editable on the confirm sheet. */
export interface HotelBookingGuestDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

/**
 * Progress and outcome of the booking the system runs for a Book press.
 * Written by the backend onto `userResponse.hotelBooking` and pushed as
 * `RESPONSE_ARTIFACT_UPDATED`.
 */
export type HotelArtifactBookingState =
  | { state: 'booking'; startedAt: string }
  | {
      state: 'booked';
      /** `pending` = placed, awaiting the hotel — never shown as confirmed. */
      status: 'confirmed' | 'pending';
      bookingId: string;
      hotelConfirmationCode: string | null;
      hotelName: string;
      room: string;
      board: string;
      checkIn: string;
      checkOut: string;
      guest: { firstName: string; lastName: string };
      total: number;
      currency: string;
      cancellationTerms: string;
      updatedAt: string;
    }
  | {
      state: 'not_booked';
      reason:
        | 'price_changed'
        | 'terms_changed'
        | 'offer_expired'
        | 'already_booked'
        | 'missing_guest_details'
        | 'insufficient_balance'
        | 'payment_failed'
        | 'declined'
        | 'rejected';
      current?: { total: number; currency: string; cancellationTerms: string };
      /** Only once the saved card was actually taken from; `failed` = ops are refunding by hand. */
      refund?: {
        status: 'pending' | 'failed';
        amount: number;
        currency: string;
      };
      updatedAt: string;
    }
  | { state: 'unknown'; bookingId: string | null; updatedAt: string }
  | {
      /** A booked stay the user has since cancelled through the assistant. */
      state: 'cancelled';
      bookingId: string;
      hotelName: string;
      checkIn: string;
      checkOut: string;
      total: number;
      currency: string;
      cancellationFee: number;
      refund: {
        /** Only `settled` means the money is back; `failed` = ops are returning it by hand. */
        status: 'none' | 'pending' | 'settled' | 'failed';
        amount: number;
        currency: string;
        destination: string;
      };
      updatedAt: string;
    }
  | {
      /** Dream is taking the exact total from the saved card; books automatically once paid. */
      state: 'awaiting_payment';
      bookingId: string;
      paymentId: string;
      taskId: string;
      cardLast4: string | null;
      updatedAt: string;
    };

export type HotelArtifactUserResponse = NonNullable<
  MultipleChoiceArtifact['userResponse']
> & {
  selectedOfferId?: string;
  guest?: HotelBookingGuestDetails;
  hotelBooking?: HotelArtifactBookingState;
};

/** The selection the client sends back when the user presses Book on a hotel. */
export interface HotelBookingSelection {
  choiceId: string;
  /** Exact rate the user pressed Book on (instant hotels only). */
  offerId?: string;
  /** Guest details as confirmed on the sheet (instant hotels only). */
  guest?: HotelBookingGuestDetails;
  /** The hotel's live quote the user approved on the sheet (instant hotels only). */
  quoteId?: string;
}

const HOTELS_VIEW = 'hotels';

/**
 * A Book press whose live quote dream-be no longer holds
 * (`409 hotel_quote_expired`). Nothing was approved, charged or queued: the
 * sheet asks the hotel again and the user re-approves the current figure, so
 * hosts rethrow this rather than treating it as a failed submit.
 */
export function isHotelQuoteExpiredError(err: unknown): boolean {
  const response = (
    err as { response?: { status?: number; data?: { code?: unknown } } } | null
  )?.response;
  return (
    response?.status === 409 && response.data?.code === 'hotel_quote_expired'
  );
}

export function isHotelsView(view: string | undefined): boolean {
  return view === HOTELS_VIEW;
}

export function asHotelChoice(choice: ArtifactChoice): HotelChoice {
  return choice;
}

export function getFromPrice(choice: ArtifactChoice): number | undefined {
  return asHotelChoice(choice).fromPrice;
}

export function getHotelOffer(choice: ArtifactChoice): HotelOffer | undefined {
  return asHotelChoice(choice).hotelOffer;
}

export function getSelectedOfferId(
  artifact: MultipleChoiceArtifact,
): string | undefined {
  const response = artifact.userResponse as
    | HotelArtifactUserResponse
    | undefined;
  return response?.selectedOfferId;
}

export function readHotelBookingState(
  userResponse: unknown,
): HotelArtifactBookingState | null {
  let parsed: unknown = userResponse;
  if (typeof userResponse === 'string') {
    try {
      parsed = JSON.parse(userResponse);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const state = (parsed as HotelArtifactUserResponse).hotelBooking;
  return state && typeof state === 'object' && 'state' in state ? state : null;
}

/** The press is still being worked on server-side; the outcome is yet to arrive. */
export function isInFlightBookingState(
  state: HotelArtifactBookingState | null,
): boolean {
  return state?.state === 'booking' || state?.state === 'awaiting_payment';
}

export function isSettledBookingState(
  state: HotelArtifactBookingState | null,
): boolean {
  return state != null && !isInFlightBookingState(state);
}

/** One line on why the previous Book press did not book, for the matrix banner. */
export function notBookedSummary(
  state: Extract<HotelArtifactBookingState, { state: 'not_booked' }>,
): string {
  const why = notBookedReason(state);
  if (!state.refund) return why;
  const amount = formatMoney(state.refund.amount, state.refund.currency);
  return state.refund.status === 'pending'
    ? `${why} The ${amount} taken from your card is being refunded.`
    : `${why} The ${amount} taken from your card is being refunded by hand — not returned yet.`;
}

export type HotelCancelledState = Extract<
  HotelArtifactBookingState,
  { state: 'cancelled' }
>;

/** What the hotel kept and where the rest went, for the cancelled banner. */
export function cancelledSummary(state: HotelCancelledState): string {
  const fee =
    state.cancellationFee > 0
      ? `The hotel kept ${formatMoney(state.cancellationFee, state.currency)} of the ${formatMoney(state.total, state.currency)} paid.`
      : `Free cancellation — nothing was kept of the ${formatMoney(state.total, state.currency)} paid.`;
  const refund = state.refund;
  if (refund.status === 'none' || refund.amount <= 0) {
    return `${fee} Nothing is due back.`;
  }
  const amount = formatMoney(refund.amount, refund.currency);
  switch (refund.status) {
    case 'settled':
      return `${fee} ${amount} has been returned to ${refund.destination}.`;
    case 'failed':
      return `${fee} ${amount} is due back to ${refund.destination} but hasn't been returned yet — the team is arranging it by hand.`;
    default:
      return `${fee} ${amount} is being returned to ${refund.destination}.`;
  }
}

function notBookedReason(
  state: Extract<HotelArtifactBookingState, { state: 'not_booked' }>,
): string {
  switch (state.reason) {
    case 'price_changed':
      return state.current
        ? `The price changed before it could be booked — the hotel now quotes ${formatMoney(state.current.total, state.current.currency)}.`
        : 'The price changed before it could be booked.';
    case 'terms_changed':
      return 'The cancellation terms changed before it could be booked.';
    case 'offer_expired':
      return 'That rate is no longer available.';
    case 'already_booked':
      return 'That rate was already booked.';
    case 'missing_guest_details':
      return 'Guest details were missing.';
    case 'insufficient_balance':
      return 'There was not enough balance to book it.';
    case 'payment_failed':
      return 'The payment did not go through.';
    default:
      return "The hotel couldn't take that booking.";
  }
}

export function guestComplete(guest: HotelBookingGuestDetails): boolean {
  return (
    guest.firstName.trim().length > 0 &&
    guest.lastName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email.trim())
  );
}

export function trimGuest(
  guest: HotelBookingGuestDetails,
): HotelBookingGuestDetails {
  const phone = guest.phone?.trim();
  return {
    firstName: guest.firstName.trim(),
    lastName: guest.lastName.trim(),
    email: guest.email.trim(),
    ...(phone ? { phone } : {}),
  };
}

export function hotelPhotos(choice: ArtifactChoice): string[] {
  const offer = getHotelOffer(choice);
  const fromOffer = offer?.photos ?? [];
  const fromChoice = [choice.imageUrl, ...(choice.additionalImages ?? [])];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of [...fromOffer, ...fromChoice]) {
    if (url && !seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

export interface HotelPhotoSlide {
  src: string;
  /** Hotel name for hotel-level photos, room name for room photos. */
  title: string;
}

/** Every photo for a hotel — hotel gallery first, then each room's — deduped. */
export function hotelPhotoSlides(choice: ArtifactChoice): HotelPhotoSlide[] {
  const offer = getHotelOffer(choice);
  const seen = new Set<string>();
  const out: HotelPhotoSlide[] = [];
  const add = (url: string | null | undefined, title: string) => {
    if (url && !seen.has(url)) {
      seen.add(url);
      out.push({ src: url, title });
    }
  };
  for (const url of hotelPhotos(choice)) add(url, choice.choiceTitle);
  for (const room of offer?.rooms ?? []) {
    for (const url of [room.photo, ...(room.photos ?? [])]) add(url, room.name);
  }
  return out;
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** `YYYY-MM-DD` → "Tue 22 Apr" (calendar date, no timezone shift). */
export function formatStayDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export function formatStay(stay: HotelOfferStay): string {
  const nights = `${stay.nights} night${stay.nights === 1 ? '' : 's'}`;
  const guests = [
    `${stay.adults} adult${stay.adults === 1 ? '' : 's'}`,
    stay.children > 0
      ? `${stay.children} child${stay.children === 1 ? '' : 'ren'}`
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  return `${formatStayDate(stay.checkInDate)} – ${formatStayDate(stay.checkOutDate)} · ${nights} · ${guests}`;
}

/** ISO timestamp → "Mon 21 Apr, 08:00" in the viewer's timezone. */
export function formatDeadline(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function cancellationLabel(rate: HotelOfferRate): string {
  if (rate.refundable) {
    return rate.freeCancellationUntil
      ? `Free cancellation until ${formatDeadline(rate.freeCancellationUntil)}`
      : 'Free cancellation';
  }
  return 'Non-refundable';
}

export function ratingLabel(rating: HotelGuestRating): {
  score: string;
  detail: string;
} {
  const score =
    rating.outOf === 10
      ? rating.score.toFixed(1)
      : `${rating.score.toFixed(1)}/5`;
  const reviews =
    rating.reviewCount != null
      ? `${rating.reviewCount.toLocaleString()} ${rating.source === 'google' ? 'Google reviews' : 'reviews'}`
      : rating.source === 'google'
        ? 'Google rating'
        : 'Guest rating';
  return { score, detail: reviews };
}

export function includesBreakfast(rate: HotelOfferRate): boolean {
  return /breakfast|half board|full board|all.inclusive/i.test(rate.board);
}

export function roomSummary(room: HotelOfferRoom): string {
  return [
    room.beds,
    room.sleeps != null ? `Sleeps ${room.sleeps}` : null,
    room.sizeSqm != null ? `${Math.round(room.sizeSqm)} m²` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function findRate(
  offer: HotelOffer | undefined,
  offerId: string | undefined,
): { room: HotelOfferRoom; rate: HotelOfferRate } | null {
  if (!offer || !offerId) return null;
  for (const room of offer.rooms ?? []) {
    const rate = room.rates.find((r) => r.offerId === offerId);
    if (rate) return { room, rate };
  }
  return null;
}
